import { AiModal } from '@aigc/components';
import { useOverlayRef } from '@aigc/overlay-hook';
import { PlusOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { App, Form, Input, Upload } from 'antd';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import type { UploadFileOutputDto } from '../../../features/file/model';
import { createBeforeUpload, FileValidators } from '../../file';
import { usePreviewFn } from '../../file/hooks';
import { fileSchema } from '../../file/schema';
import { createAigcAssetMutation } from '../api';

/** 最大文件大小 30MB */
const MAX_FILE_SIZE = 30 * 1024 * 1024;

/** 宽高比范围 */
const MIN_ASPECT_RATIO = 0.4;
const MAX_ASPECT_RATIO = 2.5;

/** 宽高像素范围 */
const MIN_DIMENSION = 300;
const MAX_DIMENSION = 6000;

export function AssetCreateModal() {
  const overlay_ref = useOverlayRef();
  const onPreview = usePreviewFn();
  const { message, notification } = App.useApp();

  const form = useForm({
    mode: 'onChange',
    defaultValues: { name: '' },
    resolver: yupResolver(
      object({
        name: string()
          .required('请输入资产名称')
          .max(64, '名称不能超过64个字符'),
        file: fileSchema,
      }),
    ),
  });

  const create = useMutation(createAigcAssetMutation);

  const beforeUpload = createBeforeUpload({
    validator: FileValidators.compose([
      FileValidators.accept(
        '.jpg,.jpeg,.png,.webp,.bmp,.tiff,.gif,.heic,.heif',
      ),
      FileValidators.maxSize(MAX_FILE_SIZE, { exclusive: true }),
      FileValidators.dimensions({ min: MIN_DIMENSION, max: MAX_DIMENSION }),
      FileValidators.aspectRatio({
        min: MIN_ASPECT_RATIO,
        max: MAX_ASPECT_RATIO,
      }),
    ]),
    onError: ({ title, message }) => {
      notification.error({ title, description: message });
    },
  });

  return (
    <AiModal
      title="新增人物"
      confirmLoading={create.isPending}
      onCancel={() => overlay_ref.close()}
      onOk={form.handleSubmit(async (values) => {
        if (create.isPending) return;

        await create.mutateAsync({
          name: values.name,
          url: values.file?.[0]?.response?.file_url as string,
        });
        message.success('创建成功');
        overlay_ref.close();
      })}
    >
      <Form layout="vertical">
        <FormControl
          name="name"
          label="人物名称"
          control={form.control}
          required
          render={({ field }) => (
            <Input {...field} placeholder="请输入资产名称" maxLength={64} />
          )}
        />
        <FormControl
          control={form.control}
          name="file"
          label="人物图片"
          required
          render={({ field }) => (
            <Upload<UploadFileOutputDto>
              {...field}
              listType="picture-card"
              accept=".jpg,.jpeg,.png,.webp,.bmp,.tiff,.gif,.heic,.heif"
              beforeUpload={beforeUpload}
              maxCount={1}
              fileList={field.value ?? []}
              onPreview={onPreview}
              onChange={(info) => {
                field.onChange(info.fileList);
              }}
            >
              {!field.value?.length && (
                <div>
                  <PlusOutlined />
                  <div className="mt-2">点击上传</div>
                </div>
              )}
            </Upload>
          )}
        ></FormControl>
      </Form>
    </AiModal>
  );
}
